/**
 * Page « Je signale un incident » — déclaration administré (L13.16.3).
 *
 * Recalée sur le mockup `mockup_admin_declaration_incident_mobile_MODERNE.html` :
 *   - ÉCRAN A : formulaire en UN SEUL écran (fini le wizard 3 étapes) —
 *     photo OBLIGATOIRE, localisation, catégorie en grille de cartes, urgence,
 *     sujet, description.
 *   - ÉCRAN B : confirmation avec cercle de succès, n° de référence en mono,
 *     timeline « Et après ? », CTA « Suivre cette fiche ».
 *
 * Photo : upload réel vers Cloudflare R2 (storage.service.ts est branché —
 * `uploadPhoto(id, 'admin_initial', file)`). La photo est OBLIGATOIRE pour
 * envoyer. L'upload se fait après la création du signalement ; en cas d'échec
 * réseau, le signalement reste créé et l'écran B le signale (la photo pourra
 * être rajoutée depuis la fiche) — la photo ne re-crée jamais le signalement.
 *
 * Localisation : « Utiliser ma position » capture les coordonnées GPS via le
 * navigateur, puis interroge l'API BAN (api-adresse.data.gouv.fr — publique,
 * gratuite, sans clé) pour convertir les coordonnées en adresse postale.
 */

import { useEffect, useState, useRef, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { Button, LogoMark, SectionEyebrow, Alert } from '@/components/ui';
import { RappelLoiBanner } from '@/components/RappelLoiBanner';
import {
  ArrowLeft, AlertCircle, Construction, TreePine, Lightbulb,
  Trash2, Droplets, MoreHorizontal, CircleAlert, Circle, AlertTriangle,
  CheckCircle2, Landmark, Hammer, Camera, MapPin, Loader2, Send,
  UserCheck, Wrench, ClipboardCheck, X, ImagePlus,
} from 'lucide-react';
import type { SignalementCategorie, SignalementUrgence } from '@roronoa-collectivites/shared-types';

const MAX_PHOTO_MB = 10;

const CATEGORIES: { value: SignalementCategorie; label: string; icon: typeof Construction }[] = [
  { value: 'voirie',         label: 'Voirie',         icon: Construction },
  { value: 'espaces_verts',  label: 'Espaces verts',  icon: TreePine },
  { value: 'eclairage',      label: 'Éclairage',      icon: Lightbulb },
  { value: 'proprete',       label: 'Propreté',       icon: Trash2 },
  { value: 'assainissement', label: 'Assainissement', icon: Droplets },
  { value: 'autre',          label: 'Autre',          icon: MoreHorizontal },
];

// Mappings statiques pour Tailwind purge-safe
const URGENCE_STYLES: Record<SignalementUrgence, { active: string; icon: typeof AlertTriangle }> = {
  urgent: { active: 'border-urgent/60 bg-urgent/15 text-urgent',   icon: AlertTriangle },
  rapide: { active: 'border-warning/60 bg-warning/15 text-warning', icon: CircleAlert },
  normal: { active: 'border-success/60 bg-success/15 text-success', icon: Circle },
};

const URGENCES: { value: SignalementUrgence; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'rapide', label: 'Rapide' },
  { value: 'normal', label: 'Normal' },
];

/**
 * Reverse-geocoding via l'API Base Adresse Nationale (gratuite, sans clé).
 * Renvoie le libellé d'adresse le plus proche, ou null si indisponible.
 */
async function reverseGeocodeBAN(lat: number, lon: number): Promise<string | null> {
  try {
    const res = await fetch(`https://api-adresse.data.gouv.fr/reverse/?lat=${lat}&lon=${lon}`);
    if (!res.ok) return null;
    const data = await res.json();
    const label = data?.features?.[0]?.properties?.label;
    return typeof label === 'string' && label.trim().length > 0 ? label : null;
  } catch {
    return null;
  }
}

export function PublicReportPage() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();

  // 3.1 — Mode de déclaration.
  // Un administré déclare toujours pour lui : aucun choix affiché.
  // Tout agent connecté (quel que soit son service : GRC, accueil, scolaire…)
  // peut saisir POUR un habitant venu au guichet ou qui appelle.
  // Le mode « constat du service » (source interne) est réservé au SERVICE
  // TECHNIQUE : c'est une intervention que personne n'a demandée.
  const estStaff = !!user && user.role !== 'administre';
  const estServiceTechnique =
    !!user && (
      user.role === 'super_admin' || user.role === 'admin' ||
      user.service === 'service_technique' ||
      (user.servicesAdditionnels ?? []).includes('service_technique')
    );
  const [mode, setMode] = useState<'moi' | 'guichet' | 'telephone' | 'interne'>('moi');
  const [declarantNom, setDeclarantNom] = useState('');
  const [declarantEmail, setDeclarantEmail] = useState('');
  const [declarantTel, setDeclarantTel] = useState('');
  const pourUnAdministre = mode === 'guichet' || mode === 'telephone';

  const [categorie, setCategorie] = useState<SignalementCategorie | null>(null);
  const [urgence, setUrgence] = useState<SignalementUrgence>('normal');
  const [sujet, setSujet] = useState('');
  const [description, setDescription] = useState('');
  const [adresse, setAdresse] = useState('');

  // Photo (obligatoire) — uploadée vers R2 après création du signalement.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Capture de position (géoloc navigateur + reverse-geocoding BAN).
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [geoCoords, setGeoCoords] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoFailed, setPhotoFailed] = useState(false);
  // M8 (retour Manon) — photo obligatoire ou non : la commune décide pour les
  // administrés, elle est toujours facultative pour le personnel. Tant que la
  // règle n'est pas connue, on garde l'exigence (comportement historique).
  const [photoObligatoire, setPhotoObligatoire] = useState(true);
  useEffect(() => {
    api.serviceTechnique.signalements.reglesDeclaration()
      .then((r) => setPhotoObligatoire(r.photoObligatoire))
      .catch(() => setPhotoObligatoire(true));
  }, []);
  const [created, setCreated] = useState<{ numero: string; id: string } | null>(null);

  const canSubmit =
    (!photoObligatoire || !!photoFile) &&
    !!categorie &&
    sujet.trim().length >= 3 &&
    description.trim().length >= 10 &&
    !loading;

  function handlePhotoPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Le fichier sélectionné n’est pas une image.');
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setError(`La photo dépasse ${MAX_PHOTO_MB} Mo — choisissez une image plus légère.`);
      return;
    }
    setError(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function clearPhoto() {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function captureGeolocation() {
    if (!('geolocation' in navigator)) {
      setGeoStatus('error');
      return;
    }
    setGeoStatus('loading');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const ns = latitude >= 0 ? 'N' : 'S';
        const ew = longitude >= 0 ? 'E' : 'O';
        const coords = `${Math.abs(latitude).toFixed(5)}°${ns} · ${Math.abs(longitude).toFixed(5)}°${ew}`;
        setGeoCoords(coords);
        // Reverse-geocoding BAN : coordonnées → adresse postale.
        const label = await reverseGeocodeBAN(latitude, longitude);
        setAdresse((prev) => (prev.trim() ? prev : (label ?? `Position GPS : ${coords}`)));
        setGeoStatus('done');
      },
      () => setGeoStatus('error'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function submit() {
    if (!categorie || (photoObligatoire && !photoFile)) return;
    setError(null);
    setLoading(true);

    // 1. Création du signalement.
    let result: { numero: string; id: string };
    try {
      result = await api.serviceTechnique.signalements.create({
        sujet: sujet.trim(),
        description: description.trim(),
        categorie,
        urgence,
        localisationAdresse: adresse.trim() || undefined,
        source:
          mode === 'interne' ? 'interne'
          : mode === 'guichet' ? 'accueil_guichet'
          : mode === 'telephone' ? 'accueil_telephone'
          : 'web',
        // Coordonnées de l'habitant seulement quand l'agent saisit pour lui.
        ...(pourUnAdministre ? {
          createurNom: declarantNom.trim() || undefined,
          createurEmail: declarantEmail.trim() || undefined,
          createurTelephone: declarantTel.trim() || undefined,
        } : {}),
      });
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error);
      else setError('Une erreur est survenue.');
      setLoading(false);
      return;
    }

    // 2. Upload de la photo vers R2. Le signalement EXISTE déjà : un échec ici
    //    ne doit jamais le re-créer — on le signale simplement sur l'écran B.
    let photoOk = true;
    if (photoFile) {   // M8 — sans photo (facultative), rien à envoyer
      try {
        const photoCompressed = await compressImageIfNeeded(photoFile);
        await api.serviceTechnique.signalements.uploadPhoto(result.id, 'admin_initial', photoCompressed);
      } catch {
        photoOk = false;
      }
    }

    setPhotoFailed(!photoOk);
    setCreated({ numero: result.numero, id: result.id });
    setLoading(false);
  }

  // ==========================================================================
  // ÉCRAN B — Confirmation (conforme mockup, écran B)
  // ==========================================================================
  if (created) {
    return (
      <div className="min-h-screen text-ink">
        <header
          className="sticky top-0 z-20 flex items-center justify-center px-4 py-3 border-b border-border backdrop-blur-glass"
          style={{
            backgroundImage:
              'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
          }}
        >
          <span className="font-display text-sm font-semibold tracking-r-snug text-ink">
            Bien reçu, merci&nbsp;!
          </span>
        </header>

        <main className="mx-auto flex max-w-md flex-col px-4 py-8 animate-fade-in">
          {/* Cercle de succès */}
          <div className="flex flex-col items-center text-center">
            <div
              className="flex h-[72px] w-[72px] items-center justify-center rounded-full border border-success/40 bg-success-tint/40 text-success"
              style={{ boxShadow: '0 0 0 8px oklch(var(--c-success) / 0.06), 0 12px 30px -10px oklch(var(--c-success) / 0.5)' }}
              aria-hidden="true"
            >
              <CheckCircle2 className="h-9 w-9" strokeWidth={2.2} />
            </div>
            <h1 className="mt-4 font-display text-lg font-semibold tracking-r-snug text-ink">
              Votre signalement est arrivé
            </h1>
            <p className="mt-1 text-sm text-ink-muted">La mairie a été notifiée.</p>
          </div>

          {/* Carte n° de référence */}
          <div className="mt-6 rounded-r-xl border border-tech/30 bg-tech-tint/30 backdrop-blur-glass p-4 text-center">
            <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              N° de référence
            </div>
            <div className="mt-1 font-mono text-lg font-bold tracking-r-tight text-tech-fg">
              {created.numero}
            </div>
          </div>

          {/* Photo non envoyée — non bloquant */}
          {photoFailed && (
            <Alert variant="warning" icon={AlertTriangle} className="mt-4">
              Votre signalement est bien enregistré, mais la photo n’a pas pu être
              envoyée. Vous pourrez la rajouter depuis la fiche de suivi.
            </Alert>
          )}

          {/* Timeline « Et après ? » */}
          <SectionEyebrow as="div" className="mt-6">Et après&nbsp;?</SectionEyebrow>
          <ol className="mt-3 space-y-0">
            <NextStep icon={CheckCircle2} title="Signalement reçu" meta="à l'instant · pris en compte" done />
            <NextStep icon={UserCheck} title="Affectation à un agent" meta="sous 24 h en général" />
            <NextStep icon={Wrench} title="Intervention terrain" meta="vous serez notifié" />
            <NextStep icon={ClipboardCheck} title="Résolution" meta="photo après ajoutée à la fiche" last />
          </ol>

          {/* Actions */}
          <Button
            variant="primary"
            className="mt-7 w-full"
            onClick={() => navigate(`/service-technique/signalements/${created.id}`)}
          >
            Suivre cette fiche
          </Button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-3 text-center text-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-sm"
          >
            Retour au tableau de bord
          </button>
        </main>
      </div>
    );
  }

  // ==========================================================================
  // ÉCRAN A — Formulaire de déclaration (écran unique)
  // ==========================================================================
  return (
    <div className="min-h-screen text-ink">
      {/* Header */}
      <header
        className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b border-border backdrop-blur-glass"
        style={{
          backgroundImage:
            'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
            'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="inline-flex h-10 w-10 items-center justify-center rounded-r-lg border border-border bg-surface/60 text-ink-muted backdrop-blur-glass transition-all hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          aria-label="Retour au tableau de bord"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
          <span className="font-display text-sm font-semibold tracking-r-snug text-ink">
            {tenant?.nom ?? '—'}
          </span>
        </div>
        <div className="w-10" aria-hidden="true" />
      </header>

      <main className="mx-auto max-w-md px-4 py-6 animate-fade-in space-y-6">
        {/* Hero */}
        <div className="flex items-center gap-3">
          <LogoMark icon={Hammer} accent="tech" size="lg" aria-label="Service technique" />
          <div>
            <SectionEyebrow as="div">Service technique</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-tight text-ink">
              Je signale un incident
            </h1>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

        {/* 3.1 — Pour qui déclare-t-on ? Visible uniquement pour le personnel.
            Un administré ne voit rien de ce bloc et déclare pour lui-même. */}
        {estStaff && (
          <section>
            <SectionEyebrow as="h2">Cette déclaration concerne</SectionEyebrow>
            <div className="mt-2 flex flex-wrap gap-2">
              {([
                { v: 'moi' as const, label: 'Moi-même' },
                { v: 'guichet' as const, label: 'Un administré · au guichet' },
                { v: 'telephone' as const, label: 'Un administré · par téléphone' },
                ...(estServiceTechnique
                  ? [{ v: 'interne' as const, label: 'Constat du service' }]
                  : []),
              ]).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMode(opt.v)}
                  className={`rounded-r-md border px-3 py-2 text-sm transition-colors ${
                    mode === opt.v
                      ? 'border-tech bg-tech-tint/30 font-medium text-ink'
                      : 'border-border text-ink-muted hover:border-tech/40'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {mode === 'interne' && (
              <p className="mt-2 text-xs text-ink-subtle">
                Intervention constatée par le service — aucun administré n'est à l'origine
                de la demande.
              </p>
            )}

            {pourUnAdministre && (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-ink-muted">Nom de l'administré</span>
                  <input
                    type="text" value={declarantNom} maxLength={150}
                    onChange={(e) => setDeclarantNom(e.target.value)}
                    className="mt-1 h-11 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-ink-muted">Email (optionnel)</span>
                  <input
                    type="email" value={declarantEmail} maxLength={255}
                    onChange={(e) => setDeclarantEmail(e.target.value)}
                    className="mt-1 h-11 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-ink-muted">Téléphone (optionnel)</span>
                  <input
                    type="tel" value={declarantTel} maxLength={30}
                    onChange={(e) => setDeclarantTel(e.target.value)}
                    className="mt-1 h-11 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                  />
                </label>
              </div>
            )}
          </section>
        )}

        {/* Photo — obligatoire ou facultative selon le déclarant (M8) */}
        <section>
          <div className="flex items-baseline justify-between">
            <SectionEyebrow as="h2">Photo</SectionEyebrow>
            <span className={`text-2xs font-medium uppercase tracking-r-eyebrow ${photoObligatoire ? 'text-urgent' : 'text-ink-subtle'}`}>
              {photoObligatoire ? 'Obligatoire' : 'Facultative'}
            </span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoPick}
            className="sr-only"
            aria-label="Prendre ou choisir une photo de l'incident"
          />

          {!photoFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 flex w-full flex-col items-center justify-center gap-2 rounded-r-xl border border-dashed border-border-strong bg-surface/40 backdrop-blur-glass px-4 py-8 text-center transition-all hover:border-tech/50 hover:bg-surface/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-r-xl border border-tech/30 bg-tech-tint/50 text-tech-fg">
                <Camera className="h-6 w-6" aria-hidden="true" />
              </span>
              <span className="text-sm font-medium text-ink">Prendre une photo</span>
              <span className="text-xs text-ink-subtle">
                Une photo aide les équipes à intervenir plus vite
              </span>
            </button>
          ) : (
            <div className="mt-2 flex items-center gap-3 rounded-r-xl border border-border bg-surface/50 backdrop-blur-glass p-2.5">
              <img
                src={photoPreview ?? ''}
                alt="Aperçu de l'incident"
                className="h-16 w-16 shrink-0 rounded-r-lg border border-border object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{photoFile.name}</p>
                <p className="text-xs text-ink-subtle tabular-nums">
                  {(photoFile.size / (1024 * 1024)).toFixed(1)} Mo
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-tech-fg hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-sm"
                >
                  <ImagePlus className="h-3.5 w-3.5" aria-hidden="true" />
                  Changer
                </button>
              </div>
              <button
                type="button"
                onClick={clearPhoto}
                aria-label="Retirer la photo"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-r-lg border border-border text-ink-muted transition-all hover:border-urgent/40 hover:bg-urgent/10 hover:text-urgent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </section>

        {/* Localisation */}
        <section>
          <SectionEyebrow as="h2">Localisation</SectionEyebrow>
          <input
            id="adresse"
            type="text"
            value={adresse}
            onChange={(e) => setAdresse(e.target.value)}
            maxLength={255}
            placeholder="Rue, repère — ou utilisez votre position"
            className="mt-2 h-12 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
          />

          <button
            type="button"
            onClick={captureGeolocation}
            disabled={geoStatus === 'loading'}
            className="mt-2 inline-flex items-center gap-2 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 py-2 text-sm text-ink-muted transition-all hover:text-ink hover:border-border-strong disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            {geoStatus === 'loading'
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <MapPin className="h-4 w-4" aria-hidden="true" />}
            {geoStatus === 'loading' ? 'Localisation en cours…' : 'Utiliser ma position actuelle'}
          </button>

          {geoStatus === 'done' && geoCoords && (
            <div className="mt-2 flex items-center gap-2.5 rounded-r-lg border border-success/30 bg-success-tint/30 backdrop-blur-glass px-3 py-2.5">
              <MapPin className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-success">Position renseignée</div>
                <div className="font-mono text-2xs text-ink-muted">{geoCoords}</div>
              </div>
            </div>
          )}
          {geoStatus === 'error' && (
            <p className="mt-2 text-xs text-ink-subtle">
              Position non disponible — saisissez l'adresse à la main.
            </p>
          )}
        </section>

        {/* Catégorie */}
        <section>
          <SectionEyebrow as="h2">Catégorie</SectionEyebrow>
          <div className="mt-2 grid grid-cols-2 gap-2.5">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isSelected = categorie === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategorie(cat.value)}
                  className={[
                    'flex items-center gap-2.5 rounded-r-xl border backdrop-blur-glass px-3 py-3 text-left transition-all duration-200 ease-out-expo active:scale-[0.98]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                    isSelected
                      ? 'border-tech/50 bg-tech-tint/70 text-tech-fg shadow-glow-tech'
                      : 'border-border bg-surface/40 text-ink hover:border-tech/30 hover:bg-surface/60',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-r-lg border',
                      isSelected
                        ? 'border-tech/30 bg-tech-tint/60 text-tech-fg'
                        : 'border-border-strong bg-surface-hi text-ink-muted',
                    ].join(' ')}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-medium">{cat.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Urgence */}
        <section>
          <SectionEyebrow as="h2">Niveau d'urgence</SectionEyebrow>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {URGENCES.map((u) => {
              const isSelected = urgence === u.value;
              const Icon = URGENCE_STYLES[u.value].icon;
              return (
                <button
                  key={u.value}
                  type="button"
                  onClick={() => setUrgence(u.value)}
                  className={[
                    'flex flex-col items-center gap-1 rounded-r-lg border backdrop-blur-glass px-2 py-3 text-xs font-medium transition-all duration-200 active:scale-95',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                    isSelected
                      ? URGENCE_STYLES[u.value].active
                      : 'border-border bg-surface/40 text-ink-muted hover:bg-surface/60',
                  ].join(' ')}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span>{u.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Sujet */}
        <section>
          <label htmlFor="sujet" className="block">
            <SectionEyebrow as="span">Sujet court</SectionEyebrow>
          </label>
          <input
            id="sujet"
            type="text"
            value={sujet}
            onChange={(e) => setSujet(e.target.value)}
            maxLength={200}
            placeholder="Ex : Nid-de-poule rue du Port"
            className="mt-2 h-12 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
          />
        </section>

        {/* Description */}
        <section>
          <label htmlFor="desc" className="block">
            <SectionEyebrow as="span">Description détaillée</SectionEyebrow>
          </label>
          <textarea
            id="desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={5000}
            rows={4}
            placeholder="Ex : Nid-de-poule très large, dangereux pour les vélos, au croisement avec la rue de l'Église."
            className="mt-2 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-3 text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
          />
          <p className="mt-1 text-xs text-ink-subtle tabular-nums">
            {description.trim().length} caractère{description.trim().length !== 1 ? 's' : ''}
            {description.trim().length < 10 && ' · 10 minimum'}
          </p>
        </section>

        {/* Rappel à la loi — item 2.4.
            Posé JUSTE AVANT le bouton d'envoi : c'est le dernier moment où la
            personne le lit, et cette page cumule les deux supports visés par
            l'item (une photo ET un texte libre).
            Ne s'affiche que si la commune a configuré un texte (mig. 201). */}
        <RappelLoiBanner />

        {/* CTA */}
        <Button variant="primary" className="w-full" onClick={submit} loading={loading} disabled={!canSubmit}>
          {!loading && <Send className="h-4 w-4" aria-hidden="true" />}
          {loading ? 'Envoi en cours…' : 'Envoyer à la mairie'}
        </Button>
        {!canSubmit && !loading && (
          <p className="-mt-3 text-center text-xs text-ink-subtle">
            {!photoFile
              ? 'Ajoutez une photo, choisissez une catégorie et décrivez l’incident.'
              : 'Choisissez une catégorie et décrivez l’incident (sujet + description).'}
          </p>
        )}
      </main>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Étape de la timeline « Et après ? » de l'écran de confirmation
// ----------------------------------------------------------------------------

function NextStep({
  icon: Icon,
  title,
  meta,
  done,
  last,
}: {
  icon: typeof CheckCircle2;
  title: string;
  meta: string;
  done?: boolean;
  last?: boolean;
}) {
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          aria-hidden="true"
          className={[
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-r-lg border',
            done
              ? 'border-success/30 bg-success-tint/40 text-success'
              : 'border-border bg-surface/50 text-ink-dim',
          ].join(' ')}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        {!last && <span aria-hidden="true" className="my-1 w-px flex-1 bg-border" />}
      </div>
      <div className={last ? '' : 'pb-4'}>
        <p className={`text-sm font-medium ${done ? 'text-ink' : 'text-ink-muted'}`}>{title}</p>
        <p className="mt-0.5 text-xs text-ink-subtle">{meta}</p>
      </div>
    </li>
  );
}
